import { DatePipe, Location } from "@angular/common";
import {
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  NgForm
} from "@angular/forms";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { DateTime } from "luxon";
import { of, Subscription, throwError } from "rxjs";
import { catchError, flatMap, map } from "rxjs/operators";

import { environment } from "../../../environments/environment";
import { TicketCutoffTypeEnum } from "../../enum/cutoff-type.enum";
import { MenuConstants } from "../../infrastructure/MenuConstants";
import {
  LoanTicketType,
  Portfolio,
  TicketStatus,
  TicketType,
} from "../../model/domain/Entity";
import { Loan } from "../../model/domain/Loan";
import { AuthService } from "../../services/auth.service";
import { CutoffService } from "../../services/cutoff.service";
import { DialogService } from "../../services/dialog.service";
import { LoaderService } from "../../services/loader.service";
import { SnackbarService } from "../../services/snackbar.service";
import { TicketService } from "../../services/ticket.service";
import { ValidatorService } from "../../services/validator.service";
import { AnnotationComponent } from "../annotation/annotation.component";
import { Roles } from "../auth/auth.role";
import { TicketCutoffPermission } from "../cutoff/ticket-cutoff-type.model";
import { DadosFundoComponent } from "../dados-fundo/dados-fundo.component";

@Component({
  selector: "app-loan",
  templateUrl: "./loan.component.html",
  styleUrls: ["./loan.component.scss"],
})
export class LoanComponent implements OnInit, OnDestroy {
  @ViewChild("thisForm", { static: true }) public form: NgForm;
  @ViewChild("portfolio", { static: true }) fundComponent: DadosFundoComponent;
  @ViewChild("annotation", { static: true })
  annotationComponent: AnnotationComponent;

  dataChanged: EventEmitter<string>;

  TicketTypeEnum = TicketType;
  navigationSubscription: Subscription;

  model: Loan;
  isPosting: boolean;
  isEditing: boolean;
  canValidate: boolean;
  isNotProduction = environment.name != 'production';

  constructor(
    public validator: ValidatorService,
    private fb: FormBuilder,
    private authService: AuthService,
    private snackBarService: SnackbarService,
    private dialogService: DialogService,
    private datePipe: DatePipe,
    private loaderService: LoaderService,
    private service: TicketService,
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private cutoffService: CutoffService
  ) {
    this.navigationSubscription = this.router.events.subscribe((e: any) => {
      if (e instanceof NavigationEnd) {
        this.resetForm();
      }
    });
  }

  validators = {
    typeAction: (value) =>
      [
        LoanTicketType.NewOperation,
        LoanTicketType.EarlySettlement,
        LoanTicketType.Renewal,
      ].includes(value),
    loanType: (value) => ["Tomador", "Doador"].includes(value),
    finalGraceDate: (value) => {
      if (
        this.model.typeAction === LoanTicketType.NewOperation ||
        this.model.typeAction === LoanTicketType.Renewal
      ) {
        return (
          this.coalesce(value).length > 0 &&
          DateTime.fromFormat(value, "dd/MM/yyyy").isValid
        );
      }
      return true;
    },
    loanOperationTypeId: (value) => {
      if (this.isNewOperationRenewal()) {
        return value > 0;
      }
      return true;
    },
    settlementPeriodicity: (value) => value > 0,
    priceSettlementReference: (value) => {
      if (this.isNewOperationRenewal()) {
        return value > 0;
      }
      return true;
    },
    resourceDate: (value) =>
      this.coalesce(value).length === 0 ||
      DateTime.fromFormat(value, "dd/MM/yyyy").isValid,
    dueDate: (value) =>
      this.coalesce(value).length === 0 ||
      DateTime.fromFormat(value, "dd/MM/yyyy").isValid,
    settlementSaleDate: (value) =>
      this.coalesce(value).length === 0 ||
      DateTime.fromFormat(value, "dd/MM/yyyy").isValid,
    settlementDate: (value) =>
      this.coalesce(value).length === 0 ||
      DateTime.fromFormat(value, "dd/MM/yyyy").isValid,
  };

  ngOnInit(): void {
    this.dataChanged = new EventEmitter<string>();
    this.getTicket();
  }

  ngOnDestroy() {
    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
    }
  }

  resetForm() {
    this.model = new Loan();
    this.fundComponent.portfolio = new Portfolio();

    this.form.reset();
    this.form.resetForm();

    this.fundComponent.resetForm();

    this.annotationComponent.annotation = null;

    this.isEditing = false;
    this.canValidate = false;

    this.model.operationDate = new Date();
    this.dataChanged = new EventEmitter<string>();
    this.setEnable(true);
  }

  coalesce(value: any) {
    return value || "";
  }

  private validateAllFormFields(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach((field) => {
      const control = formGroup.get(field);
      if (control instanceof FormControl) {
        control.markAsTouched({ onlySelf: true });
      } else if (control instanceof FormGroup) {
        this.validateAllFormFields(control);
      }
    });
  }

  submit() {
    this.loaderService.addLoader();

    const editing = this.isEditing;
    this.register(this.form)
      .pipe(
        catchError((err) => {
          this.loaderService.removeLoader();

          this.snackBarService.open(err, "OK", {
            verticalPosition: "top",
            horizontalPosition: "right",
          });
          return throwError(err);
        })
      )
      .subscribe(
        (r) => {
          this.loaderService.removeLoader();

          const msg = editing ? "Boleta alterada." : "Boleta cadastrada.";

          this.snackBarService.open(msg, "OK", {
            verticalPosition: "top",
            horizontalPosition: "right",
          });
        },
        () => {}
      );
  }

  getTicket(ticketId: number = null) {
    this.model = new Loan();

    const id =
      ticketId === null ? +this.route.snapshot.paramMap.get("id") : ticketId;
    this.model.id = id;

    if (id) {
      this.service.Loan.getTicket(id).subscribe(
        (q) => {
          Object.assign(this.model, q);

          this.isEditing = true;
          this.canValidate = true;

          this.fundComponent.fillComponentFieldsLoad(this.model);

          this.model.dueDate = this.datePipe.transform(
            this.model.dueDate,
            "dd/MM/yyyy"
          ) as any;

          this.model.finalGraceDate = this.datePipe.transform(
            this.model.finalGraceDate,
            "dd/MM/yyyy"
          ) as any;

          this.dataChanged.emit("");

          this.setEnable(false);
        },
        () => this.router.navigate([MenuConstants.Tickets.Loan])
      );
    } else {
      if (!this.model.statusId) {
        this.model.operationDate = new Date();
      }
    }
  }

  register(f: NgForm, duplicate: boolean = false) {
    const editing = this.isEditing;

    if (!this.canEdit() && !duplicate) {
      return;
    }

    Object.values(f.controls).forEach((c) => {
      c.markAsTouched();
      c.updateValueAndValidity();
    });

    this.canValidate = true;

    const errors = this.getErrors();

    this.fundComponent.validatePortfolioFields();
    const fundErrors = this.fundComponent.getErrors();

    if (f.invalid || errors.length > 0 || fundErrors.length > 0) {
      return throwError("Verifique os campos destacados.");
    }

    let data = Object.assign({}, this.model);

    data.operationDate = null;
    data.statusId = null;
    data.id = null;

    data = this.fundComponent.fillPortfolioData(
      data,
      this.fundComponent.portfolio
    );

    if (data.dueDate) {
      data.dueDate = new Date(
        data.dueDate.toString().replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$2/$1/$3")
      );
    }
    if (data.finalGraceDate) {
      data.finalGraceDate = new Date(
        data.finalGraceDate
          .toString()
          .replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$2/$1/$3")
      );
    }

    if (!data.marginCredit) {
      data.marginCredit = false;
    }
    if (!data.isReversibleByDonor) {
      data.isReversibleByDonor = false;
    }

    if (data.stocksAmount) {
      data.stocksAmount = this.validator.getNumeric(data.stocksAmount);
    }

    if (data.remunerationRate) {
      data.remunerationRate = this.validator.getNumeric(data.remunerationRate);
    }

    if (data.commissionFee) {
      data.commissionFee = this.validator.getNumeric(data.commissionFee);
    }

    if (duplicate) {
      data.workflowStartDate = null;
      data.workflowEndDate = null;
    }

    data.annotations = this.annotationComponent.annotation;

    const method =
      editing && !duplicate
        ? this.service.Loan.updateTicket(this.model.id, data)
        : this.service.Loan.registerTicket(data);

    return method
      .pipe(
        catchError((err) => {
          return throwError("Erro ao cadastrar a boleta.");
        })
      )
      .pipe(
        map((loan: Loan) => {
          if (!editing) {
            this.model.id = loan.id;
            this.model.operationDate = loan.operationDate;
            this.model.statusId = loan.statusId;
            this.model.accountManagerName = loan.accountManagerName;
            this.model.accountManagerDocument = loan.accountManagerDocument;
            this.model.ticketCutoffTypeId = loan.ticketCutoffTypeId;
            this.model.portfolioFundId = loan.portfolioFundId;

            this.isEditing = true;

            const url = this.router.createUrlTree([this.model.id], {
              relativeTo: this.route,
            });
            this.location.go(url.toString());
          }

          return this.model;
        })
      );
  }

  setEnable(enabled: boolean = false) {
    setTimeout(() => {
      if (enabled) {
        this.fundComponent.setEnable(true);
        Object.values(this.form.controls).forEach((c) => {
          c.enable();
        });
      } else if (!this.canEdit()) {
        this.fundComponent.setEnable(false);
        this.annotationComponent.annotationIsDisabled = true;
        Object.values(this.form.controls).forEach((c) => {
          c.disable();
        });
      }
    }, 10);
  }

  confirmForward(): Promise<boolean> {
    if (!this.canEdit()) {
      return Promise.resolve(false);
    }

    var textConfirm = 'A boleta não poderá mais ser editada, deseja continuar?';

    if(this.isNotProduction) {
      textConfirm += '<br />ATENÇÃO: ESTE É UM AMBIENTE DE TESTE. NÃO EXECUTE OPERAÇÕES REAIS.';
    }

    return this.dialogService
      .confirm(
        "Encaminhar Boleta",
        textConfirm
      )
      .toPromise()
      .then((result) => {
        if (!result) {
          return false;
        }

        const operationDate = DateTime.fromJSDate(
          new Date(this.model.operationDate.toString())
        );

        if (operationDate.day < DateTime.local().day) {
          return this.dialogService
            .confirm(
              "Boleta Retroativa",
              "A data de operação da boleta será alterada para a data de hoje.",
              "OK",
              "Cancelar"
            )
            .toPromise();
        }

        return true;
      });
  }

  async forward(): Promise<void> {
    const cutoffPermission = await this.checkPermission();

    if (!cutoffPermission.isAllowed) {
      this.snackBarService.open(
        this.cutoffService.warningMessage +
          this.cutoffService.dateTimeOffsetToTimeString(
            cutoffPermission.timeLimit
          ),
        "OK"
      );
      return;
    }

    this.confirmForward().then((ok) => {
      if (!ok) {
        return;
      }
      this.register(this.form)
        .pipe(
          catchError((err) => {
            this.snackBarService.open(err, "OK", {
              verticalPosition: "top",
              horizontalPosition: "right",
            });
            return throwError(err);
          })
        )
        .subscribe(
          (loan: Loan) => {
            this.service.Loan.forwardTicket(loan.id).subscribe((res) => {
              this.model.statusDescription = res.statusDescription;

              this.setEnable(false);

              this.snackBarService.open("Boleta encaminhada.", "OK", {
                verticalPosition: "top",
                horizontalPosition: "right",
              });

              this.getTicket(loan.id);
            });
          },
          () => {}
        );
    });
  }

  cancel() {
    return this.dialogService
      .confirm("Cancelar Boleta", "Deseja cancelar esta boleta?")
      .pipe(
        flatMap((ok) => {
          if (ok) {
            return this.service.Loan.cancelDraftTicket(this.model.id);
          }

          return of(null);
        })
      )
      .subscribe((res) => {
        if (res) {
          this.model.statusDescription = res.statusDescription;

          Object.values(this.form.controls).forEach((c) => {
            c.disable();
          });

          this.snackBarService.open("Boleta cancelada.", "OK", {
            verticalPosition: "top",
            horizontalPosition: "right",
          });

          this.getTicket(this.model.id);
        }
      });
  }

  approve() {
    this.dialogService
      .confirm("Aprovar Boleta", "Deseja aprovar esta boleta?")
      .subscribe((result) => {
        if (result) {
          this.service.approve(this.model.id, "").subscribe(() => {
            this.snackBarService.open("Boleta atualizada com sucesso.", "OK", {
              verticalPosition: "top",
              horizontalPosition: "right",
            });

            this.getTicket(this.model.id);
          });
        }
      });
  }

  reject(): void {
    this.dialogService
      .reject("Reprovar Boleta", "Deseja reprovar esta boleta?")
      .subscribe((result) => {
        if (result) {
          this.service
            .reject(this.model.id, result.justification)
            .subscribe(() => {
              this.snackBarService.open("Boleta atualizada com sucesso.", "OK");
              this.getTicket(this.model.id);
            });
        }
      });
  }

  duplicate() {
    this.register(this.form, true).subscribe((newTicket) => {
      this.snackBarService
        .open("Boleta duplicada.", "Visualizar no Monitor", {
          verticalPosition: "top",
          horizontalPosition: "right",
          duration: 6000,
        })
        .onAction()
        .subscribe((a) => {
          this.router.navigate([
            MenuConstants.Todos,
            { ticketId: TicketType.Loan },
          ]);
        });
    });
  }
  getErrors(): string[] {
    const validationErrors = [];

    Object.keys(this.validators).forEach((k) => {
      if (!this.validators[k](this.model[k])) {
        validationErrors.push(k);
      }
    });
    return validationErrors;
  }

  canEdit() {
    return (
      !this.model.statusId &&
      !this.model.statusDescription &&
      this.authService.hasRole(Roles.CreateTicketFundQuota)
    );
  }

  async checkPermission(): Promise<TicketCutoffPermission> {
    let cutoffTypeId = this.model.ticketCutoffTypeId;
    let accountManagerId = this.model.accountManagerId;
    let fundId = this.model.portfolioFundId;

    if (accountManagerId === null) {
      cutoffTypeId = TicketCutoffTypeEnum.Loan;
      accountManagerId = this.authService.authToken.accountManagerId;
      fundId = this.fundComponent.portfolio.fundId;
    }
    return this.cutoffService.isAvailable(
      cutoffTypeId,
      this.model.id,
      accountManagerId,
      fundId
    );
  }

  canDuplicate() {
    const _operationDate = this.datePipe.transform(
      this.model.operationDate,
      "yyyy-MM-dd"
    );
    const _duplicateTicketDateLimit = this.datePipe.transform(
      new Date(environment.duplicateTicketDateLimit),
      "yyyy-MM-dd"
    );

    return (
      this.model.id &&
      this.authService.hasRole(Roles.CreateTicketFundQuota) &&
      !(_operationDate <= _duplicateTicketDateLimit)
    );
  }

  canChangeStatus() {
    return (
      this.model.statusId &&
      this.authService.hasRoles(this.model.statusRequiredRoles)
    );
  }

  canCancel() {
    return (
      this.model.id &&
      (!this.model.statusId || this.model.statusId === TicketStatus.Draft) &&
      this.authService.hasRole(Roles.CreateTicketFundQuota)
    );
  }

  hasError(prop: string): boolean {
    if (!this.canValidate) {
      return false;
    }

    return !this.validators[prop](this.model[prop]);
  }

  isSettlementRenewal() {
    return [LoanTicketType.EarlySettlement, LoanTicketType.Renewal].includes(
      this.model.typeAction
    );
  }

  isNewOperationRenewal() {
    return [LoanTicketType.NewOperation, LoanTicketType.Renewal].includes(
      this.model.typeAction
    );
  }
}
